// Simple sound effects (no audio files) using Web Audio API.
// We keep it lightweight for MVP and avoid bundling assets.

let ctx = null;

function getAudioContext() {
  if (typeof window === 'undefined') return null;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!ctx) ctx = new AC();
  // Browsers often start suspended until user gesture; calling resume is safe.
  if (ctx.state === 'suspended') {
    // Fire and forget.
    ctx.resume().catch(() => {});
  }
  return ctx;
}

function makeNoiseBuffer(audioCtx, seconds) {
  const sampleRate = audioCtx.sampleRate;
  const length = Math.max(1, Math.floor(sampleRate * seconds));
  const buffer = audioCtx.createBuffer(1, length, sampleRate);
  const data = buffer.getChannelData(0);

  // White noise with a quick exponential-ish decay baked in.
  for (let i = 0; i < length; i += 1) {
    const t = i / length;
    const decay = Math.pow(1 - t, 3);
    data[i] = (Math.random() * 2 - 1) * 0.8 * decay;
  }
  return buffer;
}

export function playMoveSound() {
  const audioCtx = getAudioContext();
  if (!audioCtx) return;

  try {
    const now = audioCtx.currentTime;

    // Noise burst (the "piece touches board" click)
    const noise = audioCtx.createBufferSource();
    noise.buffer = makeNoiseBuffer(audioCtx, 0.045);

    const hp = audioCtx.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.setValueAtTime(600, now);

    const lp = audioCtx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(4200, now);

    const gain = audioCtx.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.18, now + 0.004);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.06);

    noise.connect(hp);
    hp.connect(lp);
    lp.connect(gain);
    gain.connect(audioCtx.destination);

    noise.start(now);
    noise.stop(now + 0.07);

    // Tiny "thunk" tone layer to make it feel less like pure noise.
    const osc = audioCtx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(220, now);

    const toneGain = audioCtx.createGain();
    toneGain.gain.setValueAtTime(0.0001, now);
    toneGain.gain.exponentialRampToValueAtTime(0.06, now + 0.006);
    toneGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.045);

    osc.connect(toneGain);
    toneGain.connect(audioCtx.destination);

    osc.start(now);
    osc.stop(now + 0.055);
  } catch {
    // Ignore audio errors (autoplay policies, device issues, etc.).
  }
}

export function playSuccessSound() {
  const audioCtx = getAudioContext();
  if (!audioCtx) return;

  try {
    const now = audioCtx.currentTime;

    const osc = audioCtx.createOscillator();
    osc.type = 'sine';

    const gain = audioCtx.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.08, now + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);

    // Little "ding" up
    osc.frequency.setValueAtTime(660, now);
    osc.frequency.exponentialRampToValueAtTime(990, now + 0.08);

    osc.connect(gain);
    gain.connect(audioCtx.destination);

    osc.start(now);
    osc.stop(now + 0.24);
  } catch {
    // ignore
  }
}

export function playErrorSound() {
  const audioCtx = getAudioContext();
  if (!audioCtx) return;

  try {
    const now = audioCtx.currentTime;

    const osc = audioCtx.createOscillator();
    osc.type = 'square';

    const gain = audioCtx.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.05, now + 0.004);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.12);

    // Quick "buzz" down
    osc.frequency.setValueAtTime(220, now);
    osc.frequency.exponentialRampToValueAtTime(150, now + 0.09);

    const lp = audioCtx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(900, now);

    osc.connect(lp);
    lp.connect(gain);
    gain.connect(audioCtx.destination);

    osc.start(now);
    osc.stop(now + 0.13);
  } catch {
    // ignore
  }
}

export function playRewardSound() {
  const audioCtx = getAudioContext();
  if (!audioCtx) return;

  try {
    const now = audioCtx.currentTime;

    const master = audioCtx.createGain();
    master.gain.setValueAtTime(0.0001, now);
    master.gain.exponentialRampToValueAtTime(0.10, now + 0.01);
    master.gain.exponentialRampToValueAtTime(0.0001, now + 0.65);
    master.connect(audioCtx.destination);

    const notes = [523.25, 659.25, 783.99, 1046.5]; // C5 E5 G5 C6
    notes.forEach((f, i) => {
      const t = now + i * 0.085;
      const osc = audioCtx.createOscillator();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(f, t);

      const g = audioCtx.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.10, t + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.22);

      osc.connect(g);
      g.connect(master);
      osc.start(t);
      osc.stop(t + 0.24);
    });

    // Soft shimmer layer
    const shimmer = audioCtx.createOscillator();
    shimmer.type = 'sine';
    shimmer.frequency.setValueAtTime(1760, now);

    const shGain = audioCtx.createGain();
    shGain.gain.setValueAtTime(0.0001, now);
    shGain.gain.exponentialRampToValueAtTime(0.015, now + 0.02);
    shGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.4);

    shimmer.connect(shGain);
    shGain.connect(master);
    shimmer.start(now);
    shimmer.stop(now + 0.42);
  } catch {
    // ignore
  }
}
